import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Field,
  IconBtn,
  Input,
  PageHeader,
  Spinner,
  Table,
} from "../../design-system";
import PortalLayout from "./PortalLayout";
import useInspectionPortal from "./useInspectionPortal";
import { formatInstant } from "../../helpers/datetime";

const ACTION_META = {
  create: { label: "Creación", tone: "success" },
  update: { label: "Modificación", tone: "info" },
  hard_delete: { label: "Eliminación definitiva", tone: "danger" },
  restore: { label: "Restauración", tone: "warning" },
};

const TYPE_LABEL = {
  time_entry: "Fichaje",
  workday_record: "Jornada",
  workday_break: "Pausa",
};

const REASON_LABEL = {
  clear_workday: "Vaciado de jornada",
  upsert_replace: "Reemplazo en edición",
  correction_apply: "Corrección aprobada",
  retention_purge: "Purga por retención (4 años)",
};

export default function PortalAuditoria() {
  document.title = "Inspección · Auditoría | Kronaly";
  return (
    <PortalLayout>
      <Body />
    </PortalLayout>
  );
}

function Body() {
  const { http, workspace } = useInspectionPortal();
  const tz = workspace?.timezone;

  const today = useMemo(() => isoYmd(new Date()), []);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoYmd(d);
  }, []);

  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [actionFilter, setActionFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await http.get("/inspection/audits", {
        params: { start_date: startDate, end_date: endDate },
      });
      setAudits(r?.data?.data || r?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.message || "Error al cargar el log.");
    } finally {
      setLoading(false);
    }
  }, [http, startDate, endDate]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    return audits.filter((a) => {
      if (actionFilter && a.action !== actionFilter) return false;
      if (typeFilter && a.auditable_type !== typeFilter) return false;
      return true;
    });
  }, [audits, actionFilter, typeFilter]);

  return (
    <>
      <PageHeader
        title="Registro inmutable de cambios"
        description="Todas las modificaciones del registro horario están aquí registradas con hash criptográfico encadenado. Cualquier alteración posterior se detecta."
      />

      <Alert tone="info">
        <strong>Append-only.</strong> Las entradas de este log no se modifican ni se eliminan desde la
        aplicación. Si necesitas comprobar la integridad de la cadena, solicita al responsable que
        ejecute <code>php artisan time-tracking:verify-audit-chain</code> — debe devolver "Cadena íntegra".
      </Alert>

      {error && <Alert tone="danger">{error}</Alert>}

      <div style={filterRow}>
        <Field label="Desde">
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Hasta">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        <Field label="Acción">
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={selectStyle}>
            <option value="">Todas</option>
            <option value="create">Creación</option>
            <option value="update">Modificación</option>
            <option value="hard_delete">Eliminación definitiva</option>
          </select>
        </Field>
        <Field label="Tipo">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            <option value="time_entry">Fichaje</option>
            <option value="workday_record">Jornada</option>
            <option value="workday_break">Pausa</option>
          </select>
        </Field>
        <Button variant="primary" icon="RefreshCw" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Buscar"}
        </Button>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="FileSearch"
          title="Sin eventos en el rango"
          description="Ningún evento coincide con los filtros aplicados."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <th style={th}>Fecha y hora</th>
              <th style={th}>Acción</th>
              <th style={th}>Recurso</th>
              <th style={th}>Actor</th>
              <th style={th}>IP</th>
              <th style={th}>Motivo</th>
              <th style={{ ...th, textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => {
              const action = ACTION_META[a.action] || { label: a.action, tone: "neutral" };
              return (
                <tr key={a.id}>
                  <td style={{ ...td, fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                    {formatDateTime(a.occurred_at, tz)}
                  </td>
                  <td style={td}>
                    <Badge tone={action.tone}>{action.label}</Badge>
                  </td>
                  <td style={td}>
                    {TYPE_LABEL[a.auditable_type] || a.auditable_type}
                    <div style={subtleId}>{(a.auditable_id || "").slice(0, 8)}…</div>
                  </td>
                  <td style={td}>
                    {a.actor_user_id ? (
                      <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                        {a.actor_user_id.slice(0, 8)}…
                      </span>
                    ) : (
                      <Badge tone="neutral">Sistema</Badge>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                    {a.ip || "—"}
                  </td>
                  <td style={td}>{a.reason ? REASON_LABEL[a.reason] || a.reason : "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <IconBtn icon="Eye" title="Ver detalle" onClick={() => setDetail(a)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Drawer open={detail !== null} onClose={() => setDetail(null)} title="Detalle del evento" size="lg">
        {detail && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <pre style={jsonBox}>
              <strong>Antes:</strong>
              {"\n"}
              {detail.before_json ? JSON.stringify(detail.before_json, null, 2) : "—"}
            </pre>
            <pre style={jsonBox}>
              <strong>Después:</strong>
              {"\n"}
              {detail.after_json ? JSON.stringify(detail.after_json, null, 2) : "—"}
            </pre>
          </div>
        )}
      </Drawer>
    </>
  );
}

function isoYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Instante en la zona del workspace de inspección.
function formatDateTime(iso, tz) {
  return formatInstant(iso, tz, {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const th = {
  textAlign: "left",
  padding: "10px 16px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};

const subtleId = {
  fontFamily: "var(--kr-font-mono)",
  fontSize: 11,
  color: "var(--kr-fg-muted)",
  marginTop: 2,
};

const filterRow = {
  display: "flex",
  gap: 12,
  alignItems: "flex-end",
  flexWrap: "wrap",
  marginBottom: 16,
};

const selectStyle = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--kr-border)",
  background: "var(--kr-bg)",
  color: "var(--kr-fg)",
  fontSize: 14,
  minWidth: 160,
};

const jsonBox = {
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
  padding: 12,
  fontFamily: "var(--kr-font-mono)",
  fontSize: 12,
  maxHeight: 240,
  overflow: "auto",
  margin: 0,
  whiteSpace: "pre-wrap",
};
